import React, { useEffect, useState, lazy, Suspense } from 'react'
import {
    getDocs,
    collection,
    query,
    where,
    orderBy,
    limit,
    documentId,
} from 'firebase/firestore'
import Skeleton from 'react-loading-skeleton'
import { db } from '../system/firebase/index'
import {
    getCollectionList,
    getCollectionListBySearch,
} from '../helpers/ListHandler'
import { getInitialFeed } from '../helpers/FeedHandler'
import { useLocation } from '@reach/router'

const Grid = lazy(() => import('../components/feeds/Grid'))
const Carousel = lazy(() => import('../components/feeds/Carousel'))
const Team = lazy(() => import('../components/feeds/Team'))
const Testimonials = lazy(() => import('./Testimonials'))

const Feed = ({ options, data, auxData }) => {
    const location = useLocation()
    const [feed, setData] = useState(false)
    const [queryAddition, setAddition] = useState(false)
    let timeOutId = null

    const initialFeed = getInitialFeed(options, auxData)

    // This is a custom sort function for the cars-for-sale/available feed
    function customCarsForSaleSort(a, b) {
        // if a car category contains "new-entry" then it should be at the top of the list
        // if a car category contains "recently-sold" then it should be at the bottom of the list
        // otherwise sort by date_modified

        if (a.categories.includes("new-entry")) {
            return -1
        }
        if (b.categories.includes("new-entry")) {
            return 1
        }
        if (a.categories.includes("recently-sold")) {
            return 1
        }
        if (b.categories.includes("recently-sold")) {
            return -1
        }
        if (a.date_modified.seconds > b.date_modified.seconds) {
            return -1
        }
        if (a.date_modified.seconds < b.date_modified.seconds) {
            return 1
        }

        return 0
    }

    // This is a custom sort function for the cars-for-sale/sold feed
    function customSoldCarsSort(a, b) {
        // if a car category contains "recently-sold" then it should be at the top of the list
        // otherwise sort by date_modified

        if (a.categories.includes("recently-sold") && !b.categories.includes("recently-sold")) {
            return -1
        }
        if (b.categories.includes("recently-sold") && !a.categories.includes("recently-sold")) {
            return 1
        }
        if (a.date_modified.seconds > b.date_modified.seconds) {
            return -1
        }
        if (a.date_modified.seconds < b.date_modified.seconds) {
            return 1
        }

        return 0
    }

    useEffect(() => {
        let isCancelled = false
        const getRelatedDocs = async () => {
            const result = await getCollectionList({
                category: ['recently-sold'],
                limitNumber: 9,
            })
            return result
        }
        if (options.type !== 'testimonials') {
            const sortedData = { collection: '', docs: [] }
            if (options.data) {
                if (Array.isArray(options.data)) {
                    // We have 2 feed sequential of each other
                    const promiseArray = []
                    Object.entries(options.data).map(([key, objData]) => {
                        if (
                            objData.collection === 'collectionSaleProfiles' ||
                            objData.collection === 'collectionWebProfiles' ||
                            objData.collection === 'collectionSearch'
                        ) {
                            sortedData.collection = 'collectionSearch'
                            promiseArray.push(
                                getCollectionList({
                                    category: objData.categories || [],
                                    by: objData.order.by,
                                    direction: objData.order.direction,
                                    limitNumber: objData.limit || 1000,
                                }).then((collectionResult) => {
                                    collectionResult.list.forEach((doc) => {
                                        sortedData.docs.push(doc)
                                    })
                                })
                            )
                        } else {
                            const constraints = []

                            if (objData.categories) {
                                constraints.push(
                                    where(
                                        'categories',
                                        'array-contains-any',
                                        objData.categories
                                    )
                                )
                                constraints.push(
                                    where('visibility', '==', 'public')
                                )
                            }
                            if (objData.order) {
                                constraints.push(
                                    orderBy(
                                        objData.order.by,
                                        objData.order.direction
                                    )
                                )
                            }
                            if (objData.limit) {
                                constraints.push(limit(objData.limit))
                            }

                            const q = query(
                                collection(db, objData.collection),
                                ...constraints
                            )
                            sortedData.collection = objData.collection
                            promiseArray.push(
                                getDocs(q).then((querySnapshot) => {
                                    querySnapshot.forEach((doc) => {
                                        sortedData.docs.push(doc.data())
                                    })
                                })
                            )
                        }
                    })
                    Promise.all(promiseArray).then(() => {
                        // add hard-coded sort for cars-for-sale/available and homepage feed
                        if (
                            location?.pathname === '/cars-for-sale/available' 
                            || location?.pathname === '/cars-for-sale/available/'
                            || location?.pathname === ''
                            || location?.pathname === '/'
                        ) {
                            sortedData.docs.sort(customCarsForSaleSort)
                        }

                        if (!isCancelled) {
                            setData(sortedData)
                        }
                    })
                } else {
                    if (
                        queryAddition &&
                        (queryAddition.search !== '' || queryAddition.filter)
                    ) {
                        timeOutId = setTimeout(() => {
                            getCollectionListBySearch({
                                category: options.data.categories || [],
                                whereArray: queryAddition.filter.where || [],
                                term: queryAddition.search,
                                perPage: options.data.limit || 1000,
                            }).then((collectionResult) => {
                                // HERE
                                sortedData.collection = options.data.collection
                                sortedData.docs = collectionResult.list
                                setData(sortedData)
                            })
                        }, 500)
                    } else {
                        if (
                            options.data.collection ===
                                'collectionSaleProfiles' ||
                            options.data.collection === 'collectionSearch'
                        ) {
                            getCollectionList({
                                category: options.data.categories || [],
                                by: options.data.order.by,
                                direction: options.data.order.direction,
                                limitNumber: options.data.limit || 1000,
                            }).then((collectionResult) => {
                                sortedData.collection = 'collectionSearch'
                                collectionResult.list.forEach((doc) => {
                                    sortedData.docs.push(doc)
                                })
                                if (
                                    options.data.type &&
                                    options.data.type === 'curated'
                                ) {
                                    const orderDocs = []
                                    // Here we are rearranging the array so that it is in the same order as the curated feed.
                                    Object.entries(options.data.set).map(
                                        ([key, id]) => {
                                            orderDocs.push(
                                                sortedData.docs.find((doc) => {
                                                    return doc.id === id
                                                })
                                            )
                                        }
                                    )
                                    sortedData.docs = orderDocs
                                }

                                // add hard-coded sort for cars-for-sale/available and homepage feed
                                if (
                                    location?.pathname === '/cars-for-sale/available' 
                                    || location?.pathname === '/cars-for-sale/available/'
                                    || location?.pathname === ''
                                    || location?.pathname === '/'
                                ) {
                                    sortedData.docs.sort(customCarsForSaleSort)
                                }

                                // add hard-coded sort for cars-for-sale/sold feed
                                if (
                                    location?.pathname === '/cars-for-sale/sold' 
                                    || location?.pathname === '/cars-for-sale/sold/'
                                ) {
                                    sortedData.docs.sort(customSoldCarsSort)
                                }

                                setData(sortedData)
                            })
                        } else {
                            const constraints = []
                            sortedData.collection = options.data.collection
                            if (
                                options.data.type &&
                                options.data.type === 'curated'
                            ) {
                                constraints.push(
                                    where(documentId(), 'in', options.data.set)
                                )
                            } else {
                                if (options.data.categories) {
                                    constraints.push(
                                        where(
                                            'categories',
                                            'array-contains-any',
                                            options.data.categories
                                        )
                                    )
                                    if (
                                        options.data.collection ===
                                            'collectionSaleProfiles' ||
                                        options.data.collection ===
                                            'collectionSearch'
                                    ) {
                                        constraints.push(
                                            where('active', '==', true)
                                        )
                                    }
                                    constraints.push(
                                        where('visibility', '==', 'public')
                                    )
                                }
                                if (options.data.order && !queryAddition) {
                                    constraints.push(
                                        orderBy(
                                            options.data.order.by,
                                            options.data.order.direction
                                        )
                                    )
                                }
                                if (options.data.limit) {
                                    constraints.push(limit(options.data.limit))
                                }
                            }

                            const q = query(
                                collection(db, options.data.collection),
                                ...constraints
                            )
                            getDocs(q)
                                .then((docs) => {
                                    if (!isCancelled) {
                                        docs.forEach((doc) => {
                                            const itemData = doc.data()
                                            itemData.id = doc.id
                                            sortedData.docs.push(itemData)
                                        })
                                        if (
                                            options.data.type &&
                                            options.data.type === 'curated'
                                        ) {
                                            const orderDocs = []
                                            // Here we are rearranging the array so that it is in the same order as the curated feed.
                                            Object.entries(
                                                options.data.set
                                            ).map(([key, id]) => {
                                                orderDocs.push(
                                                    sortedData.docs.find(
                                                        (doc) => {
                                                            return doc.id === id
                                                        }
                                                    )
                                                )
                                            })
                                            sortedData.docs = orderDocs
                                        }
                                        setData(sortedData)
                                    }
                                })
                                .catch((error) => {
                                    // console.log('Error getting documents: ', error);
                                })
                        }
                    }
                }
            } else {
                let q = null
                switch (options.variation) {
                    case 'related':
                        sortedData.collection = 'posts'
                        q = query(collection(db, 'posts'), limit(9))

                        getDocs(q)
                            .then((docs) => {
                                if (!isCancelled) {
                                    docs.forEach((doc) => {
                                        sortedData.docs.push(doc.data())
                                    })
                                    setData(sortedData)
                                }
                            })
                            .catch((error) => {
                                // console.log('Error getting documents: ', error);
                            })
                        break
                    default:
                        sortedData.collection = 'collectionSearch'

                        sortedData.docs = getRelatedDocs()
                        if (!isCancelled) {
                            setData(sortedData)
                        }
                        break
                }
            }
        }

        return () => {
            isCancelled = true
        }
    }, [options, queryAddition])

    const renderUpdate = (filters) => {
        clearTimeout(timeOutId)
        setAddition(filters)
    }

    let output = null
    switch (options.type) {
        case 'carousel':
            output = (
                <Carousel
                    initialFeed={initialFeed}
                    options={options}
                    data={feed}
                    pageData={data}
                />
            )
            break
        case 'team':
            output = (
                <Team
                    initialFeed={initialFeed}
                    options={options}
                    data={feed}
                    pageData={data}
                />
            )
            break
        case 'testimonials':
            output = (
                <Testimonials
                    options={options}
                    pageData={data}
                />
            )
            break
        default:
            output = (
                <Grid
                    initialFeed={initialFeed}
                    options={options}
                    data={feed}
                    pageData={data}
                    renderUpdate={renderUpdate}
                />
            )
    }
    return <Suspense fallback={<Skeleton height={447} />}>{output}</Suspense>
}

export default Feed
